import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import ConfirmModal from 'Components/Modal/ConfirmModal';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from '../components/TdarrTableRowCell';
import { kinds } from 'Helpers/Props';
import ActivityActionMenu from '../components/ActivityActionMenu';
import { tdarrCrud } from '../api';
import { useTdarrCollection } from '../queries';

const columns = [
  { name: 'key', label: 'Name', isVisible: true, isSortable: false },
  { name: 'value', label: 'Value', isVisible: true, isSortable: false },
  { name: 'template', label: 'Template', isVisible: true, isSortable: false },
  { name: 'actions', label: 'Actions', isVisible: true, isSortable: false }
];

export default function Variables() {
  const queryClient = useQueryClient();
  const query = useTdarrCollection('VariablesJSONDB');
  const variables = useMemo(() => query.data?.filter((item) => item.type === 'global') ?? [], [query.data]);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState('');
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);
  useEffect(() => { setValues(Object.fromEntries(variables.map((item) => [item._id, String(item.value ?? '')]))); }, [variables]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'VariablesJSONDB'] });
  const create = useMutation({
    mutationFn: () => tdarrCrud({ collection: 'VariablesJSONDB', mode: 'insert', docID: '', obj: { key: name.trim(), value, type: 'global', date: Date.now() } }),
    onSuccess: async () => { setName(''); setValue(''); await refresh(); setMessage({ kind: 'success', text: 'Global variable added.' }); },
    onError: () => setMessage({ kind: 'danger', text: 'Global variable could not be added.' })
  });
  const update = useMutation({
    mutationFn: (id: string) => tdarrCrud({ collection: 'VariablesJSONDB', mode: 'update', docID: id, obj: { _id: id, value: values[id] ?? '', type: 'global' } }),
    onSuccess: async () => { await refresh(); setMessage({ kind: 'success', text: 'Global variable saved.' }); },
    onError: () => setMessage({ kind: 'danger', text: 'Global variable could not be saved.' })
  });
  const remove = useMutation({
    mutationFn: (id: string) => tdarrCrud({ collection: 'VariablesJSONDB', mode: 'removeOne', docID: id, obj: {} }),
    onSuccess: async () => { setDeleteId(''); await refresh(); setMessage({ kind: 'success', text: 'Global variable deleted.' }); },
    onError: () => { setDeleteId(''); setMessage({ kind: 'danger', text: 'Global variable could not be deleted.' }); }
  });
  const duplicate = variables.some((item) => item.key === name.trim());
  const canCreate = Boolean(name.trim() && value && !duplicate);
  return <PageContent title="Global Variables"><PageContentBody>
    <FieldSet legend="Add Global Variable">
      <Alert>Use variables in plugins as <code>{'{{{args.userVariables.global.variableName}}}'}</code>.</Alert>
      <FormGroup><FormLabel name="variableName">Name</FormLabel><TextInput name="variableName" value={name} onChange={({ value: next }) => setName(String(next))} /></FormGroup>
      <FormGroup><FormLabel name="variableValue">Value</FormLabel><TextInput name="variableValue" value={value} onChange={({ value: next }) => setValue(String(next))} /></FormGroup>
      {duplicate && <Alert kind={kinds.WARNING}>A global variable with this name already exists.</Alert>}
      <Button kind={kinds.PRIMARY} isDisabled={!canCreate || create.isPending} onPress={() => create.mutate()}>{create.isPending ? 'Adding…' : 'Add'}</Button>
    </FieldSet>
    <FieldSet legend="Global Variables">
      {message && <Alert kind={message.kind}>{message.text}</Alert>}
      {query.isError && <Alert kind={kinds.DANGER}>Unable to load global variables.</Alert>}
      {query.isSuccess && !variables.length && <Alert>No global variables are configured.</Alert>}
      {!!variables.length && <Table columns={columns}><TableBody>{variables.map((item) => <TableRow key={item._id}>
        <TableRowCell>{String(item.key ?? item._id)}</TableRowCell>
        <TableRowCell><TextInput name={`value-${item._id}`} value={values[item._id] ?? ''} onChange={({ value: next }) => setValues((current) => ({ ...current, [item._id]: String(next) }))} /></TableRowCell>
        <TableRowCell><code>{`{{{args.userVariables.global.${String(item.key ?? item._id)}}}}`}</code></TableRowCell>
        <TableRowCell><ActivityActionMenu actions={[{ key: 'save', label: 'Save' }, { key: 'delete', label: 'Delete' }]} isSpinning={(update.isPending && update.variables === item._id) || (remove.isPending && deleteId === item._id)} onAction={(action) => action === 'save' ? update.mutate(item._id) : setDeleteId(item._id)} /></TableRowCell>
      </TableRow>)}</TableBody></Table>}
    </FieldSet>
    <ConfirmModal isOpen={Boolean(deleteId)} kind="danger" title="Delete Global Variable" message={`Delete ${String(variables.find((item) => item._id === deleteId)?.key ?? deleteId)}?`} confirmLabel="Delete" isSpinning={remove.isPending} onConfirm={() => remove.mutate(deleteId)} onCancel={() => setDeleteId('')} />
  </PageContentBody></PageContent>;
}
